import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { Box, Button, Dialog, FormControl, IconButton, MenuItem, Select, Switch, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EastIcon from '@mui/icons-material/East';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import { EVENT_TYPE_META } from './PrivateLessonStyled';

const KIND_LABEL = {
  PRIVATE_LESSON: '개인레슨',
  DUET_LESSON: '듀엣레슨',
  OT: 'OT',
  CONSULTATION: '상담',
  ETC: '기타 일정',
};

// 유형 배지 색 — 스케줄 카드와 같은 유형색
const KIND_META = {
  PRIVATE_LESSON: EVENT_TYPE_META.pt,
  DUET_LESSON: EVENT_TYPE_META.duet,
  OT: EVENT_TYPE_META.ot,
  CONSULTATION: EVENT_TYPE_META.consult,
  ETC: EVENT_TYPE_META.etc,
};

const PRIVATE_KINDS = ['PRIVATE_LESSON', 'DUET_LESSON', 'OT'];
const BRAND = '#0F7F85';
const dayLabel = date => moment(date, 'YYYY-MM-DD').format('M월 D일 (dd)');

// 기존 → 변경 일시 블록
const SlotBlock = ({ caption, date, time, highlight }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 0,
      p: '10px 12px',
      borderRadius: '8px',
      border: `1px solid ${highlight ? 'rgba(15, 127, 133, 0.35)' : '#E6E8EB'}`,
      backgroundColor: highlight ? 'rgba(15, 127, 133, 0.06)' : '#F7F8FA',
    }}>
    <Typography sx={{ fontSize: '11px', fontWeight: 600, color: highlight ? BRAND : '#8A929A', mb: '2px' }}>
      {caption}
    </Typography>
    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#1F2A37' }}>{dayLabel(date)}</Typography>
    <Typography sx={{ fontSize: '13px', color: highlight ? BRAND : '#5B6470', fontWeight: 600 }}>{time}</Typography>
  </Box>
);

const selectSx = {
  minWidth: '76px',
  '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#fff' },
  '& .MuiSelect-select': { py: '7px', fontSize: '14px', fontWeight: 600 },
};

const DAY_END_MINUTES = 24 * 60;
const HOURS = Array.from({ length: 24 }, (_, hour) => hour);
const MINUTE_STEP = 5;
const MINUTES = Array.from({ length: 60 / MINUTE_STEP }, (_, index) => index * MINUTE_STEP);

const pad = value => String(value).padStart(2, '0');
const hhmm = time => String(time || '').slice(0, 5);
const toMinutes = time => {
  const [hour, minute] = hhmm(time).split(':').map(Number);
  return hour * 60 + (minute || 0);
};
const toTime = minutes => `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;

/**
 * 카드를 다른 칸에 놓았을 때 이동할지 복사할지 묻는 모달(일·주·월 공통).
 * drop = { event, mode('day'|'week'|'month'), newDate('YYYY-MM-DD'), newTrainer({trainerId, trainerName}, 일간에서 강사가 바뀔 때만),
 *          startTime·endTime(일간: 놓은 시각 그대로), defaultWithTime·defaultStart(주·월간: 시간 변경 기본값) }
 * 주·월간은 항상 '시간도 변경' 여부와 시작 시각을 고르게 하고, 종료는 원래 길이만큼 자동 계산한다.
 * onMove·onCopy 는 최종 { newDate, newStartTime, newEndTime } 을 받는다.
 */
const ScheduleDropModal = ({ drop, onMove, onCopy, onClose }) => {
  const [withTime, setWithTime] = useState(false);
  const [startHour, setStartHour] = useState(0);
  const [startMinute, setStartMinute] = useState(0);

  useEffect(() => {
    if (!drop || drop.mode === 'day') return;
    const start = toMinutes(drop.defaultStart);
    setWithTime(drop.defaultWithTime);
    setStartHour(Math.floor(start / 60));
    setStartMinute(start % 60);
  }, [drop]);

  if (!drop) return <Dialog open={false} />;

  const { event, mode, newDate } = drop;
  const originalStart = hhmm(event.startTime);
  const originalEnd = hhmm(event.endTime);
  const duration = toMinutes(originalEnd) - toMinutes(originalStart);
  const isDay = mode === 'day';

  let finalStart = originalStart;
  let finalEnd = originalEnd;
  let overMidnight = false;
  if (isDay) {
    finalStart = drop.startTime;
    finalEnd = drop.endTime;
    // 드래그 종료가 24:00 이상이면 유효한 시각이 아니다
    overMidnight = toMinutes(finalEnd) >= DAY_END_MINUTES || toMinutes(finalStart) < 0;
  } else if (withTime) {
    const startMinutes = startHour * 60 + startMinute;
    finalStart = toTime(startMinutes);
    finalEnd = toTime((startMinutes + duration) % DAY_END_MINUTES);
    overMidnight = startMinutes + duration >= DAY_END_MINUTES;
  }

  // 날짜도 시각도 강사도 그대로면 옮기거나 복사할 이유가 없다
  const nothingChanged =
    newDate === event.registrationStartDate && finalStart === originalStart && !drop.newTrainer;
  const disabled = overMidnight || nothingChanged;
  // 지난 시간으로의 복사는 막는다(이동은 허용, 사용자 확정 2026-09-30)
  const pastTarget = moment(`${newDate} ${finalEnd}`, 'YYYY-MM-DD HH:mm').isBefore(moment());
  const copyDisabled = disabled || pastTarget;
  const result = { newDate, newStartTime: finalStart, newEndTime: finalEnd };
  // 목록 시작 분이 5분 단위가 아니어도 선택값이 비지 않게 옵션에 포함한다
  const minuteOptions = MINUTES.includes(startMinute) ? MINUTES : [...MINUTES, startMinute].sort((a, b) => a - b);

  const kindMeta = KIND_META[event.courseClassType] || EVENT_TYPE_META.etc;
  const warning = overMidnight
    ? '일정 길이만큼 시작하면 자정을 넘깁니다. 더 이른 시각을 선택해 주세요.'
    : nothingChanged
      ? '날짜와 시간이 그대로라 옮기거나 복사할 수 없습니다.'
      : pastTarget
        ? '지난 시간으로는 복사할 수 없습니다. 이동만 가능합니다.'
        : null;

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={false}
      PaperProps={{ sx: { width: '420px', maxWidth: 'calc(100vw - 32px)', borderRadius: '12px', overflow: 'hidden' } }}>
      {/* 헤더 */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: '16px 16px 12px 20px' }}>
        <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#1F2A37' }}>일정을 옮길까요, 복사할까요?</Typography>
        <IconButton size="small" aria-label="닫기" onClick={onClose} sx={{ color: '#8A929A' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ px: '20px', pb: '16px' }}>
        {/* 대상 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '12px', minWidth: 0 }}>
          <Box
            component="span"
            sx={{
              flexShrink: 0,
              px: '8px',
              py: '2px',
              borderRadius: '999px',
              fontSize: '12px',
              fontWeight: 700,
              color: kindMeta.color,
              backgroundColor: kindMeta.badgeBg,
            }}>
            {KIND_LABEL[event.courseClassType] || '일정'}
          </Box>
          <Typography
            sx={{ fontSize: '14px', fontWeight: 600, color: '#1F2A37', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {event.memberName || event.className || '-'}
          </Typography>
        </Box>

        {/* 기존 → 변경 */}
        <Box sx={{ display: 'flex', alignItems: 'stretch', gap: '8px' }}>
          <SlotBlock caption="기존" date={event.registrationStartDate} time={`${originalStart} ~ ${originalEnd}`} />
          <Box sx={{ display: 'flex', alignItems: 'center', color: '#B0B7BF' }}>
            <EastIcon fontSize="small" />
          </Box>
          <SlotBlock caption="변경" date={newDate} time={`${finalStart} ~ ${finalEnd}`} highlight />
        </Box>

        {/* 강사 변경 (일 보기에서 다른 강사 컬럼에 놓은 경우) */}
        {drop.newTrainer && (
          <Box sx={{ mt: '10px' }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1F2A37' }}>
              강사 {event.trainerName || '-'} → <Box component="span" sx={{ color: BRAND }}>{drop.newTrainer.trainerName}</Box>
            </Typography>
            {PRIVATE_KINDS.includes(event.courseClassType) && (
              <Typography sx={{ fontSize: '12px', color: '#8A929A' }}>
                새 강사가 담당강사가 아니면 이동할 때 담당강사 처리를 묻습니다
              </Typography>
            )}
          </Box>
        )}

        {/* 시간 변경 (주·월 보기) */}
        {!isDay && (
          <Box
            sx={{
              mt: '12px',
              borderRadius: '8px',
              border: '1px solid #E6E8EB',
              p: '10px 12px',
              backgroundColor: withTime ? '#FBFCFC' : '#fff',
            }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1F2A37' }}>시간도 변경</Typography>
                <Typography sx={{ fontSize: '12px', color: '#8A929A' }}>
                  {withTime ? '종료는 원래 길이만큼 자동으로 맞춥니다' : `원래 시간(${originalStart}~${originalEnd}) 그대로`}
                </Typography>
              </Box>
              <Switch
                checked={withTime}
                onChange={e => setWithTime(e.target.checked)}
                inputProps={{ 'aria-label': '시간도 변경' }}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: BRAND },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: BRAND },
                }}
              />
            </Box>
            {withTime && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mt: '10px' }}>
                <FormControl size="small" sx={selectSx}>
                  <Select value={startHour} onChange={e => setStartHour(Number(e.target.value))} MenuProps={{ PaperProps: { sx: { maxHeight: 260 } } }}>
                    {HOURS.map(hour => (
                      <MenuItem key={hour} value={hour}>
                        {pad(hour)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography sx={{ fontWeight: 700, color: '#5B6470' }}>:</Typography>
                <FormControl size="small" sx={selectSx}>
                  <Select value={startMinute} onChange={e => setStartMinute(Number(e.target.value))} MenuProps={{ PaperProps: { sx: { maxHeight: 260 } } }}>
                    {minuteOptions.map(minute => (
                      <MenuItem key={minute} value={minute}>
                        {pad(minute)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography sx={{ fontSize: '14px', color: '#5B6470', ml: '4px' }}>~</Typography>
                <Box
                  sx={{
                    px: '10px',
                    py: '6px',
                    borderRadius: '8px',
                    backgroundColor: '#F1F3F5',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#1F2A37',
                  }}>
                  {finalEnd}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {warning && (
          <Box
            role="alert"
            sx={{
              mt: '12px',
              p: '8px 12px',
              borderRadius: '8px',
              backgroundColor: '#FDECEC',
              color: '#B42318',
              fontSize: '13px',
            }}>
            {warning}
          </Box>
        )}
      </Box>

      {/* 버튼 */}
      <Box sx={{ display: 'flex', gap: '8px', p: '12px 20px 16px', borderTop: '1px solid #F0F1F3' }}>
        <Button
          onClick={onClose}
          sx={{ flex: '0 0 auto', px: '14px', color: '#5B6470', borderRadius: '8px', fontWeight: 600 }}>
          취소
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="outlined"
          disabled={copyDisabled}
          startIcon={<ContentCopyIcon sx={{ fontSize: '16px !important' }} />}
          onClick={() => onCopy(result)}
          sx={{
            borderRadius: '8px',
            fontWeight: 700,
            px: '16px',
            color: BRAND,
            borderColor: 'rgba(15, 127, 133, 0.5)',
            '&:hover': { borderColor: BRAND, backgroundColor: 'rgba(15, 127, 133, 0.06)' },
          }}>
          복사
        </Button>
        <Button
          variant="contained"
          disableElevation
          disabled={disabled}
          startIcon={<OpenWithIcon sx={{ fontSize: '16px !important' }} />}
          onClick={() => onMove(result)}
          sx={{ borderRadius: '8px', fontWeight: 700, px: '16px', backgroundColor: BRAND, '&:hover': { backgroundColor: '#0C6B70' } }}>
          이동
        </Button>
      </Box>
    </Dialog>
  );
};

export default ScheduleDropModal;
